import { memo, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { ScrollView, View, Pressable, Platform, useWindowDimensions, ActivityIndicator, Alert, Modal, KeyboardAvoidingView, TextInput } from 'react-native';
import { YStack } from 'tamagui';
import { H4 } from 'tamagui';
import { Card } from 'tamagui';
import { XStack } from 'tamagui';
import { MaterialIcons, FontAwesome5, FontAwesome, FontAwesome6 } from '@expo/vector-icons';
import { SizableText } from 'tamagui';
import { Button } from 'tamagui';
import { AntDesign } from '@expo/vector-icons';
import moment from 'moment';
import { Image } from 'react-native';
import styles from '@stylesheet';
import { ToolbarContext } from '@hooks/toolbarContext';
import { Text } from 'tamagui';
import { Sheet, Popover, Select, TextArea } from 'tamagui';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { CheckBox } from '@constants/Themed';
import { AI_API_URL, HOST } from '@api/constant';
import { monitorFail, getScheduledPosts, updateScheduledPost } from '@api/api';
import * as Animatable from 'react-native-animatable';

// Schedule Tab Component
interface ScheduleTabProps {
  theme: boolean;
  data: any[];
}

// Post Preview Component - Similar to PostDesigner
const ScheduledPostPreview = memo(({ post, theme, onPress, showFullContent }: { post: any; theme: boolean; onPress?: () => void; showFullContent?: boolean }) => {
  const postImage = post.media?.[0]?.url || post.image;
  const postCaption = post.content || post.caption || post.description || '';
  // Use content as title if title is just the default "Scheduled Social Post"
  const defaultTitle = post.title === "Scheduled Social Post" && postCaption
    ? (postCaption.length > 50 ? postCaption.substring(0, 50) + '...' : postCaption)
    : (post.title || post.headline || '');
  const postHeadline = defaultTitle;
  const postHashtags = post.hashtags || [];
  const postLocation = post.location || '';

  return (
    <Pressable onPress={onPress}>
      <Card
        style={{
          backgroundColor: theme ? "#ffffff" : "#1a1a1a",
          borderRadius: 8,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
          marginBottom: 4,
        }}
      >
        {/* Post Header */}
        <XStack
          padding={8}
          alignItems="center"
          gap={8}
          backgroundColor={theme ? 'white' : '#1a1a1a'}
        >
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: theme ? '#f0f0f0' : '#333',
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: theme ? '#e5e5e5' : '#444',
            }}
          >
            <MaterialIcons
              name="storefront"
              size={16}
              color={theme ? '#666' : '#aaa'}
            />
          </View>
          <YStack flex={1} gap={2}>
            <SizableText fontWeight="600" fontSize={11} color={theme ? '#000' : '#fff'}>
              your_business
            </SizableText>
            {postLocation && (
              <SizableText fontSize={9} color={theme ? '#666' : '#999'}>
                📍 {postLocation}
              </SizableText>
            )}
          </YStack>
        </XStack>

        {/* Post Image */}
        <View
          style={{
            height: 200,
            maxHeight: '30%',
            backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {postImage ? (
            <Image
              source={{ uri: postImage }}
              style={{
                width: '100%',
                height: '100%',
              }}
              resizeMode="cover"
            />
          ) : (
            <MaterialIcons
              name="photo-library"
              size={24}
              color={theme ? '#999' : '#666'}
            />
          )}
        </View>

        {/* Post Actions Bar */}
        <XStack
          padding={8}
          alignItems="center"
          justifyContent="space-between"
          backgroundColor={theme ? 'white' : '#1a1a1a'}
        >
          <XStack gap={12}>
            <FontAwesome5
              name="heart"
              size={14}
              color={theme ? '#262626' : '#f5f5f5'}
            />
            <FontAwesome5
              name="comment"
              size={14}
              color={theme ? '#262626' : '#f5f5f5'}
            />
            <FontAwesome5
              name="share"
              size={14}
              color={theme ? '#262626' : '#f5f5f5'}
            />
          </XStack>
          <FontAwesome5
            name="bookmark"
            size={14}
            color={theme ? '#262626' : '#f5f5f5'}
          />
        </XStack>

        {/* Post Content */}
        <YStack style={{ paddingHorizontal: 8, paddingBottom: 8 }} gap={4}>
          {/* Caption */}
          {postCaption && (
            <SizableText
              fontSize={11}
              lineHeight={14}
              color={theme ? '#000' : '#fff'}
              numberOfLines={showFullContent ? undefined : 2}
            >
              <SizableText fontWeight="600">your_business</SizableText>{' '}
              {postCaption}
            </SizableText>
          )}

          {/* Headline */}
          {postHeadline && (
            <SizableText fontWeight="600" fontSize={11} color={theme ? '#000' : '#fff'}>
              "{postHeadline}"
            </SizableText>
          )}

          {/* Hashtags */}
          {postHashtags.length > 0 && (
            <SizableText fontSize={11} color="#1877f2" lineHeight={14}>
              {postHashtags.map((tag: string) => `#${tag}`).join(' ')}
            </SizableText>
          )}

          {/* Scheduled Time */}
          {post.scheduledDate && (
            <SizableText fontSize={9} color={theme ? '#8e8e8e' : '#999'} marginTop={2}>
              Scheduled: {moment(post.scheduledDate).format('MMM D, h:mm A')}
            </SizableText>
          )}
        </YStack>
      </Card>
    </Pressable>
  );
});
ScheduledPostPreview.displayName = 'ScheduledPostPreview';

const allowedList = ['linkedin', 'tiktok', 'instagram', 'x', 'facebook', 'whatsapp'];

const ScheduleTab = memo(({ theme, data }: ScheduleTabProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const { calendarContext, setCalendarContext } = useContext(ToolbarContext);
  const user = selectUser();
  const userIntegrations = user?.authentications || {};
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedIntegrations, setSelectedIntegrations] = useState<Record<string, boolean>>({ feed: true });
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  });
  const [showAIScheduler, setShowAIScheduler] = useState(false);
  const [aiSchedulerLoading, setAiSchedulerLoading] = useState(false);
  const [aiSchedulerText, setAiSchedulerText] = useState('');
  const [socialMediaPopoverOpen, setSocialMediaPopoverOpen] = useState(false);
  const [fetchedScheduledPosts, setFetchedScheduledPosts] = useState<any[]>([]);
  const [loadingScheduledPosts, setLoadingScheduledPosts] = useState(false);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [updatingPost, setUpdatingPost] = useState(false);
  const [timezonePopoverOpen, setTimezonePopoverOpen] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [expandedContinents, setExpandedContinents] = useState<Record<string, boolean>>({
    'America': true,
    'Europe': false,
    'Asia': false,
    'Africa': false,
    'Australia': false,
    'Pacific': false,
    'Atlantic': false,
    'Indian': false,
    'Antarctica': false,
  });
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Normalize calendarContext similar to CalendarHeader
    if (!calendarContext) return moment().startOf('week');
    if (moment.isMoment(calendarContext)) return calendarContext.clone().startOf('week');
    if (calendarContext instanceof Date) return moment(calendarContext).startOf('week');
    if (typeof calendarContext === 'string') {
      const parsed = moment(calendarContext, moment.ISO_8601, true);
      if (parsed.isValid()) return parsed.startOf('week');
      return moment().startOf('week');
    }
    return moment(calendarContext).startOf('week');
  });

  // Normalize calendarContext to ensure it's a valid moment object
  const normalizedCalendarContext = useMemo(() => {
    if (!calendarContext) return moment();
    if (moment.isMoment(calendarContext)) return calendarContext;
    if (calendarContext instanceof Date) return moment(calendarContext);
    if (typeof calendarContext === 'string') {
      const parsed = moment(calendarContext, moment.ISO_8601, true);
      if (parsed.isValid()) return parsed;
      return moment();
    }
    return moment(calendarContext);
  }, [calendarContext]);

  // Update currentWeekStart when calendarContext changes
  useEffect(() => {
    setCurrentWeekStart(normalizedCalendarContext.clone().startOf('week'));
  }, [normalizedCalendarContext]);

  // Fetch scheduled posts from API
  const fetchScheduledPosts = useCallback(async () => {
    if (!user?.id) return;

    setLoadingScheduledPosts(true);
    try {
      const response = await getScheduledPosts(user.id);
      if (response?.success && response?.posts) {
        setFetchedScheduledPosts(response.posts);
      }
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
    } finally {
      setLoadingScheduledPosts(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchScheduledPosts();
  }, [fetchScheduledPosts, normalizedCalendarContext]);

  // Combine data prop posts with fetched scheduled posts
  const scheduledPosts = useMemo(() => {
    const propPosts = data?.filter(post => {
      if (!post.scheduledDate) return false;
      return moment(post.scheduledDate).isAfter(moment());
    }) || [];

    // Merge with fetched posts, avoiding duplicates by ID
    const allPosts = [...propPosts];
    const existingIds = new Set(propPosts.map(p => p.id || p._id));

    fetchedScheduledPosts.forEach(post => {
      const postId = post.id || post._id;
      if (!existingIds.has(postId)) {
        allPosts.push(post);
        existingIds.add(postId);
      }
    });

    return allPosts;
  }, [data, fetchedScheduledPosts]);

  // Generate week days (Sunday to Saturday)
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = currentWeekStart.clone().add(i, 'days');
      days.push({
        date: day,
        dayName: day.format('ddd'),
        dayNumber: day.format('D'),
        isToday: day.isSame(moment(), 'day'),
        isCurrentMonth: day.isSame(normalizedCalendarContext, 'month'),
      });
    }
    return days;
  }, [currentWeekStart, normalizedCalendarContext]);

  // Generate time slots (8am to 8pm)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 8; hour <= 20; hour++) {
      slots.push({
        hour,
        label: hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`,
        time: moment().hour(hour).minute(0).second(0),
      });
    }
    return slots;
  }, []);

  // Helper function to convert date to timezone
  const convertToTimezone = useCallback((date: string | Date, timezone: string) => {
    try {
      // Use Intl API to convert to timezone
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      const parts = formatter.formatToParts(dateObj);
      const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
      const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1;
      const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');

      return moment().year(year).month(month).date(day).hour(hour).minute(0).second(0).millisecond(0);
    } catch {
      // Fallback to regular moment if timezone conversion fails
      return moment(date);
    }
  }, []);

  // Get posts for a specific day and time slot
  const getPostsForSlot = useCallback((day: moment.Moment, hour: number) => {
    return scheduledPosts.filter(post => {
      if (!post.scheduledDate) return false;
      const postDate = convertToTimezone(post.scheduledDate, selectedTimezone);
      return postDate.isSame(day, 'day') && postDate.hour() === hour;
    });
  }, [scheduledPosts, selectedTimezone, convertToTimezone]);

  // Check if a day has any posts
  const dayHasPosts = useCallback((day: moment.Moment) => {
    return scheduledPosts.some(post => {
      if (!post.scheduledDate) return false;
      const postDate = convertToTimezone(post.scheduledDate, selectedTimezone);
      return postDate.isSame(day, 'day');
    });
  }, [scheduledPosts, selectedTimezone, convertToTimezone]);

  // Get timezone options grouped by continent
  const timezoneOptionsByContinent = useMemo(() => {
    try {
      const timezones = Intl.supportedValuesOf('timeZone');
      const grouped: Record<string, Array<{ label: string; value: string }>> = {
        'UTC': [],
        'America': [],
        'Europe': [],
        'Asia': [],
        'Africa': [],
        'Australia': [],
        'Pacific': [],
        'Atlantic': [],
        'Indian': [],
        'Antarctica': [],
      };

      timezones.forEach(tz => {
        if (tz === 'UTC') {
          grouped['UTC'].push({ label: 'UTC', value: 'UTC' });
        } else {
          const parts = tz.split('/');
          const continent = parts[0];
          if (grouped[continent]) {
            grouped[continent].push({
              label: tz.replace(/_/g, ' '),
              value: tz
            });
          }
        }
      });

      // Sort each continent's timezones
      Object.keys(grouped).forEach(continent => {
        grouped[continent].sort((a, b) => a.label.localeCompare(b.label));
      });

      return grouped;
    } catch {
      return {
        'UTC': [{ label: 'UTC', value: 'UTC' }],
        'America': [
          { label: 'America/New York', value: 'America/New_York' },
          { label: 'America/Los Angeles', value: 'America/Los_Angeles' },
        ],
        'Europe': [{ label: 'Europe/London', value: 'Europe/London' }],
        'Asia': [{ label: 'Asia/Tokyo', value: 'Asia/Tokyo' }],
        'Africa': [],
        'Australia': [],
        'Pacific': [],
        'Atlantic': [],
        'Indian': [],
        'Antarctica': [],
      };
    }
  }, []);

  // Filter timezones based on search
  const filteredTimezoneOptionsByContinent = useMemo(() => {
    if (!timezoneSearch.trim()) return timezoneOptionsByContinent;

    const searchLower = timezoneSearch.toLowerCase();
    const filtered: Record<string, Array<{ label: string; value: string }>> = {};

    Object.keys(timezoneOptionsByContinent).forEach(continent => {
      const filteredTzs = timezoneOptionsByContinent[continent].filter(tz =>
        tz.label.toLowerCase().includes(searchLower) ||
        tz.value.toLowerCase().includes(searchLower)
      );
      if (filteredTzs.length > 0) {
        filtered[continent] = filteredTzs;
      }
    });

    return filtered;
  }, [timezoneOptionsByContinent, timezoneSearch]);

  // Toggle continent expansion
  const toggleContinent = useCallback((continent: string) => {
    setExpandedContinents(prev => ({
      ...prev,
      [continent]: !prev[continent]
    }));
  }, []);

  // AI Scheduling handler
  const handleAISchedule = useCallback(async () => {
    if (!aiSchedulerText.trim()) {
      Alert.alert('Input Required', 'Please enter content or idea to generate a scheduled post.');
      return;
    }

    setAiSchedulerLoading(true);
    try {
      const response = await monitorFail.post(`/ai/quick-social-content`, { text: aiSchedulerText }).then(res => res.data);

      if (!response) {
        throw new Error("No response from AI");
      }

      let content = response.post_text || response.raw?.toString().replace(/^"|"$/g, "") || "";
      if (response.hashtags) {
        content += "\n\n" + response.hashtags.join(" ");
      }

      // Here you would typically open a scheduling dialog with the generated content
      Alert.alert('Content Generated', 'AI content has been generated. You can now schedule it.');
      setAiSchedulerText('');
      setShowAIScheduler(false);
      // Refresh scheduled posts after generating content
      fetchScheduledPosts();
    } catch (error) {
      console.error("Error generating AI content:", error);
      Alert.alert("Error", "Failed to generate content. Please try again.");
    } finally {
      setAiSchedulerLoading(false);
    }
  }, [aiSchedulerText]);

  // Navigation handlers
  const handlePreviousWeek = useCallback(() => {
    const newWeek = currentWeekStart.clone().subtract(1, 'week');
    setCurrentWeekStart(newWeek);
    setCalendarContext?.(newWeek.toDate());
  }, [currentWeekStart, setCalendarContext]);

  const handleNextWeek = useCallback(() => {
    const newWeek = currentWeekStart.clone().add(1, 'week');
    setCurrentWeekStart(newWeek);
    setCalendarContext?.(newWeek.toDate());
  }, [currentWeekStart, setCalendarContext]);

  const handleToday = useCallback(() => {
    const today = moment().startOf('week');
    setCurrentWeekStart(today);
    setCalendarContext?.(today.toDate());
  }, [setCalendarContext]);

  const isCurrentWeek = currentWeekStart.isSame(moment(), 'week');

  // Calculate day column width to span full width
  const timeColumnWidth = 60;
  const dayColumnWidth = useMemo(() => {
    // Calculate available width (screen width minus time column)
    const availableWidth = screenWidth - timeColumnWidth;
    // Divide by 7 days, with a minimum width
    const calculatedWidth = availableWidth / 7;
    // Ensure minimum width for readability
    return Math.max(calculatedWidth, Platform.OS === 'web' ? 120 : 100);
  }, [screenWidth]);

  return (
    <View style={{ flex: 1 }}>
      {/* Week Navigation Header */}
      <View style={{
        backgroundColor: theme ? '#ffffff' : '#1f2937',
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e5e7eb' : '#374151',
        paddingHorizontal: 20,
        paddingVertical: 12,
      }}>
        <XStack alignItems="center" justifyContent="space-between" gap={12} flexWrap="wrap">
          <XStack alignItems="center" gap={8}>
            <Button
              size="$3"
              borderRadius={20}
              backgroundColor={theme ? "#f0f0f0" : "#2d3748"}
              onPress={handlePreviousWeek}
              icon={<AntDesign name="left" size={16} color={theme ? "#1e293b" : "#f1f5f9"} />}
            />
            <Button
              size="$3"
              borderRadius={20}
              backgroundColor={theme ? "#f0f0f0" : "#2d3748"}
              onPress={handleNextWeek}
              icon={<AntDesign name="right" size={16} color={theme ? "#1e293b" : "#f1f5f9"} />}
            />
          </XStack>
          <SizableText style={{
            fontSize: 16,
            fontWeight: '600',
            color: theme ? "#1e293b" : "#f1f5f9",
            flex: 1,
            textAlign: 'center'
          }}>
            {currentWeekStart.format('MMM D')} - {currentWeekStart.clone().add(6, 'days').format('MMM D, YYYY')}
          </SizableText>
          <XStack alignItems="center" gap={8}>
            {/* Timezone Selector */}
            <Popover open={timezonePopoverOpen} onOpenChange={setTimezonePopoverOpen}>
              <Popover.Trigger asChild>
                <Button
                  size="$3"
                  borderRadius={20}
                  backgroundColor={theme ? "#f0f0f0" : "#2d3748"}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  icon={<MaterialIcons name="access-time" size={16} />}
                >
                  {selectedTimezone.split('/').pop()?.replace(/_/g, ' ')}
                </Button>
              </Popover.Trigger>
              <Popover.Content
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                padding={16}
                minWidth={300}
                maxWidth={400}
              >
                <YStack gap={8} maxHeight={500}>
                  <SizableText fontWeight="600" fontSize={16} color={theme ? "#1e293b" : "#f1f5f9"}>
                    Select Timezone
                  </SizableText>
                  {/* Search Input */}
                  <View style={{
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: theme ? "#f8fafc" : "#374151",
                    borderWidth: 1,
                    borderColor: theme ? "#e5e7eb" : "#4b5563",
                  }}>
                    <XStack alignItems="center" gap={8}>
                      <MaterialIcons name="search" size={20} color={theme ? "#64748b" : "#9ca3af"} />
                      <TextInput
                        style={{
                          flex: 1,
                          color: theme ? "#1e293b" : "#f1f5f9",
                          fontSize: 14,
                        }}
                        placeholder="Search timezones..."
                        placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                        value={timezoneSearch}
                        onChangeText={setTimezoneSearch}
                      />
                      {timezoneSearch && (
                        <Pressable onPress={() => setTimezoneSearch('')}>
                          <MaterialIcons name="clear" size={20} color={theme ? "#64748b" : "#9ca3af"} />
                        </Pressable>
                      )}
                    </XStack>
                  </View>
                  <ScrollView
                    nestedScrollEnabled
                    showsVerticalScrollIndicator={true}
                    style={{ maxHeight: 400 }}
                  >
                    {Object.keys(filteredTimezoneOptionsByContinent).length > 0 ? (
                      Object.keys(filteredTimezoneOptionsByContinent).map((continent) => {
                        const timezones = filteredTimezoneOptionsByContinent[continent];
                        if (timezones.length === 0) return null;

                        const isExpanded = expandedContinents[continent] ?? false;

                        return (
                          <View key={continent} style={{ marginBottom: 8 }}>
                            {/* Continent Header */}
                            <Pressable
                              onPress={() => toggleContinent(continent)}
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: 12,
                                borderRadius: 8,
                                backgroundColor: theme ? "#f1f5f8" : "#1e293b",
                                marginBottom: 4,
                              }}
                            >
                              <XStack alignItems="center" gap={8}>
                                <MaterialIcons
                                  name={isExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"}
                                  size={20}
                                  color={theme ? "#1e293b" : "#f1f5f9"}
                                />
                                <SizableText
                                  fontWeight="600"
                                  fontSize={14}
                                  color={theme ? "#1e293b" : "#f1f5f9"}
                                >
                                  {continent}
                                </SizableText>
                                <SizableText
                                  fontSize={12}
                                  color={theme ? "#64748b" : "#9ca3af"}
                                >
                                  ({timezones.length})
                                </SizableText>
                              </XStack>
                            </Pressable>

                            {/* Timezones in Continent */}
                            {isExpanded && (
                              <View style={{ paddingLeft: 12 }}>
                                {timezones.map((tz) => (
                                  <Pressable
                                    key={tz.value}
                                    onPress={() => {
                                      setSelectedTimezone(tz.value);
                                      setTimezonePopoverOpen(false);
                                      setTimezoneSearch('');
                                    }}
                                    style={{
                                      padding: 10,
                                      paddingLeft: 20,
                                      borderRadius: 6,
                                      backgroundColor: selectedTimezone === tz.value
                                        ? (theme ? "#e3f2fd" : "#1e3a8a")
                                        : 'transparent',
                                    }}
                                  >
                                    <SizableText color={theme ? "#1e293b" : "#f1f5f9"}>
                                      {tz.label.replace(`${continent}/`, '')}
                                    </SizableText>
                                  </Pressable>
                                ))}
                              </View>
                            )}
                          </View>
                        );
                      })
                    ) : (
                      <View style={{ padding: 20, alignItems: 'center' }}>
                        <SizableText color={theme ? "#64748b" : "#9ca3af"}>
                          No timezones found
                        </SizableText>
                      </View>
                    )}
                  </ScrollView>
                </YStack>
              </Popover.Content>
            </Popover>

            {/* Social Media Platform Selector */}
            <Popover open={socialMediaPopoverOpen} onOpenChange={setSocialMediaPopoverOpen}>
              <Popover.Trigger asChild>
                <Button
                  size="$3"
                  borderRadius={20}
                  backgroundColor={theme ? "#f0f0f0" : "#2d3748"}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  icon={<FontAwesome name="share-alt" size={16} />}
                >
                  {Object.keys(selectedIntegrations).filter(k => selectedIntegrations[k] && k !== 'feed').length || 'Platforms'}
                </Button>
              </Popover.Trigger>
              <Popover.Content
                backgroundColor={theme ? "#fff" : "#2a2a2a"}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
                padding={16}
                minWidth={200}
              >
                <YStack gap={12}>
                  <SizableText fontWeight="600" fontSize={16} color={theme ? "#1e293b" : "#f1f5f9"}>
                    Post to Platforms
                  </SizableText>
                  {Object.keys(userIntegrations).map((key) => {
                    if (!allowedList.includes(key) || key === 'Mail' || key === 'mail') return null;

                    const getIconName = (key: string) => {
                      if (key === 'x' || key === 'X') return 'twitter';
                      if (key === 'linkedin') return 'linkedin';
                      if (key === 'facebook') return 'facebook';
                      if (key === 'instagram') return 'instagram';
                      if (key === 'tiktok') return 'music';
                      if (key === 'whatsapp') return 'whatsapp';
                      return 'share';
                    };

                    return (
                      <Pressable
                        key={key}
                        onPress={() => {
                          setSelectedIntegrations(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }));
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          paddingVertical: 8,
                          paddingHorizontal: 12,
                          borderRadius: 8,
                        }}
                      >
                        <CheckBox
                          value={selectedIntegrations[key] || false}
                          onValueChange={() => {
                            setSelectedIntegrations(prev => ({
                              ...prev,
                              [key]: !prev[key]
                            }));
                          }}
                          style={{ marginRight: 12 }}
                        />
                        <FontAwesome
                          name={getIconName(key) as any}
                          size={18}
                          color={selectedIntegrations[key] ? "rgb(90,192,234)" : (theme ? "#666" : "#999")}
                          style={{ marginRight: 12 }}
                        />
                        <SizableText color={theme ? "#1e293b" : "#f1f5f9"}>
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </SizableText>
                      </Pressable>
                    );
                  })}
                </YStack>
              </Popover.Content>
            </Popover>

            {/* AI Scheduling Button */}
            <Button
              size="$3"
              borderRadius={20}
              backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
              color="white"
              icon={<FontAwesome6 name="robot" size={16} />}
              onPress={() => setShowAIScheduler(true)}
            >
              AI Schedule
            </Button>

            {!isCurrentWeek && (
              <Button
                size="$3"
                borderRadius={20}
                backgroundColor={theme ? "#3b82f6" : "#2563eb"}
                color="white"
                onPress={handleToday}
              >
                Today
              </Button>
            )}
          </XStack>
        </XStack>
      </View>

      {/* Weekly Calendar Grid */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 20 }}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
      >
        <View style={{ flexDirection: 'row' }}>
          {/* Fixed Time Column */}
          <View style={{ width: timeColumnWidth, top: 24 }}>
            {/* Day Headers Spacer */}
            <View style={{ height: 60, paddingHorizontal: 8 }} />

            {/* Time Labels Column */}
            <View style={{ backgroundColor: theme ? "#f8fafc" : "#111827" }}>
              {timeSlots.map((slot, index) => (
                <View
                  key={index}
                  style={{
                    height: 140,
                    paddingHorizontal: 8,
                    paddingTop: 4,
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? "#e5e7eb" : "#374151",
                  }}
                >
                  <SizableText style={{
                    fontSize: 11,
                    color: theme ? "#64748b" : "#9ca3af",
                    fontWeight: '500',
                  }}>
                    {slot.label}
                  </SizableText>
                </View>
              ))}
            </View>
          </View>

          {/* Scrollable Day Columns */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={true}
            nestedScrollEnabled
            style={{ flex: 1 }}
            contentContainerStyle={{ flexDirection: 'column' }}
          >
            {/* Day Headers */}
            <View style={{ flexDirection: 'row' }}>
              {weekDays.map((day, index) => {
                const hasPosts = dayHasPosts(day.date);
                const minDayWidth = 120;
                const dayWidth = hasPosts ? Math.max(350, minDayWidth) : minDayWidth;

                return (
                  <View
                    key={index}
                    style={{
                      width: dayWidth,
                      minWidth: minDayWidth,
                      alignItems: 'center',
                      paddingVertical: 12,
                      borderLeftWidth: index > 0 ? 1 : 0,
                      borderLeftColor: theme ? "#e5e7eb" : "#374151",
                      backgroundColor: theme ? "#f8fafc" : "#111827",
                    }}
                  >
                    <SizableText style={{
                      fontSize: 11,
                      fontWeight: '600',
                      color: theme ? "#64748b" : "#9ca3af",
                      marginBottom: 4,
                    }}>
                      {day.dayName.toUpperCase()}
                    </SizableText>
                    <View style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: day.isToday
                        ? (theme ? "#3b82f6" : "#2563eb")
                        : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <SizableText style={{
                        fontSize: 14,
                        fontWeight: day.isToday ? '700' : '500',
                        color: day.isToday
                          ? 'white'
                          : (day.isCurrentMonth
                            ? (theme ? "#1e293b" : "#f1f5f9")
                            : (theme ? "#94a3b8" : "#64748b")),
                      }}>
                        {day.dayNumber}
                      </SizableText>
                    </View>
                  </View>
                );
              })}
            </View>

            {/* Calendar Grid */}
            <View style={{ flexDirection: 'row' }}>
              {weekDays.map((day, dayIndex) => {
                const hasPosts = dayHasPosts(day.date);
                const minDayWidth = 120;
                const dayWidth = hasPosts ? Math.max(350, minDayWidth) : minDayWidth;

                return (
                  <View
                    key={dayIndex}
                    style={{
                      width: dayWidth,
                      minWidth: minDayWidth,
                      borderLeftWidth: dayIndex > 0 ? 1 : 0,
                      borderLeftColor: theme ? "#e5e7eb" : "#374151",
                    }}
                  >
                    {timeSlots.map((slot, slotIndex) => {
                      const postsInSlot = getPostsForSlot(day.date, slot.hour);
                      return (
                        <Pressable
                          key={slotIndex}
                          style={{
                            height: 140,
                            borderBottomWidth: 1,
                            borderBottomColor: theme ? "#e5e7eb" : "#374151",
                            backgroundColor: theme ? "#ffffff" : "#1e293b",
                            padding: 2,
                            position: 'relative',
                          }}
                          onPress={() => {
                            // Could open a modal to schedule a post at this time
                            console.log('Schedule post at', day.date.format('YYYY-MM-DD'), slot.hour);
                          }}
                        >
                          {postsInSlot.length > 0 && (
                            <ScrollView
                              nestedScrollEnabled
                              showsVerticalScrollIndicator={false}
                            >
                              {postsInSlot.map((post, postIndex) => (
                                <ScheduledPostPreview
                                  key={post.id || post._id || postIndex}
                                  post={post}
                                  theme={theme}
                                  onPress={() => {
                                    setSelectedPost(post);
                                    setShowPostModal(true);
                                  }}
                                />
                              ))}
                            </ScrollView>
                          )}
                        </Pressable>
                      );
                    })}
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </View>

        {/* Loading State */}
        {loadingScheduledPosts && scheduledPosts.length === 0 && (
          <View style={[styles.centerEverything, {
            paddingVertical: 60,
            paddingHorizontal: 20,
          }]}>
            <ActivityIndicator
              size="large"
              color={theme ? "#3b82f6" : "#60a5fa"}
              style={{ marginBottom: 16 }}
            />
            <SizableText
              style={{
                textAlign: 'center',
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}
            >
              Loading scheduled posts...
            </SizableText>
          </View>
        )}

        {/* Empty State */}
        {!loadingScheduledPosts && scheduledPosts.length === 0 && (
          <View style={[styles.centerEverything, {
            paddingVertical: 60,
            paddingHorizontal: 20,
          }]}>
            <MaterialIcons
              name="schedule"
              size={64}
              color={theme ? "#64748b" : "#94a3b8"}
              style={{ marginBottom: 16 }}
            />
            <H4 style={{
              textAlign: 'center',
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 8
            }}>
              No Scheduled Posts
            </H4>
            <SizableText
              style={{
                textAlign: 'center',
                paddingHorizontal: 20,
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}
            >
              Schedule your posts to publish them automatically
            </SizableText>
          </View>
        )}
      </ScrollView>

      {/* Post Detail Modal */}
      <Sheet
        modal
        open={showPostModal}
        onOpenChange={(open) => {
          setShowPostModal(open);
          if (!open) {
            setIsEditingPost(false);
            setEditedContent('');
          }
        }}
        snapPoints={[85]}
        dismissOnSnapToBottom
      >
        <Sheet.Overlay
          animation="lazy"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Sheet.Handle />
        <Sheet.Frame
          padding={20}
          maxHeight="85%"
          borderTopLeftRadius={20}
          borderTopRightRadius={20}
        >
          {selectedPost && (
            <YStack gap={16} flex={1}>
              <XStack alignItems="center" justifyContent="space-between">
                <H4 style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
                  Scheduled Post Preview
                </H4>
                <Pressable onPress={() => setShowPostModal(false)}>
                  <MaterialIcons
                    name="close"
                    size={24}
                    color={theme ? "#64748b" : "#9ca3af"}
                  />
                </Pressable>
              </XStack>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{ paddingBottom: 10 }}
              >
                {!isEditingPost ? (
                  <>
                    <ScheduledPostPreview
                      post={selectedPost}
                      theme={theme}
                      showFullContent={true}
                    />
                    {/* Additional Post Details */}
                    {selectedPost.content && (
                      <YStack gap={8} marginTop={16} padding={12} backgroundColor={theme ? "#f8fafc" : "#1e293b"} borderRadius={8}>
                        <SizableText fontWeight="600" fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                          Post Content
                        </SizableText>
                        <SizableText fontSize={13} color={theme ? "#374151" : "#d1d5db"} lineHeight={20}>
                          {selectedPost.content}
                        </SizableText>
                      </YStack>
                    )}
                  </>
                ) : (
                  <YStack gap={12} marginTop={8}>
                    <SizableText fontWeight="600" fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Edit Post Content
                    </SizableText>
                    <TextArea
                      value={editedContent}
                      onChangeText={setEditedContent}
                      placeholder="Enter post content..."
                      backgroundColor={theme ? '#f8fafc' : '#374151'}
                      borderColor={theme ? '#e5e7eb' : '#4b5563'}
                      minHeight={200}
                      textAlignVertical="top"
                      color={theme ? '#1f2937' : '#ffffff'}
                      fontSize={14}
                    />
                  </YStack>
                )}
                {selectedPost.integrations && selectedPost.integrations.length > 0 && (
                  <YStack gap={8} marginTop={12} padding={12} backgroundColor={theme ? "#f8fafc" : "#1e293b"} borderRadius={8}>
                    <SizableText fontWeight="600" fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Platforms
                    </SizableText>
                    <XStack gap={8} flexWrap="wrap">
                      {selectedPost.integrations.map((platform: string, index: number) => (
                        <View
                          key={index}
                          style={{
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderRadius: 16,
                            backgroundColor: theme ? "#e5e7eb" : "#374151",
                          }}
                        >
                          <SizableText fontSize={12} color={theme ? "#1e293b" : "#f1f5f9"}>
                            {platform.charAt(0).toUpperCase() + platform.slice(1)}
                          </SizableText>
                        </View>
                      ))}
                    </XStack>
                  </YStack>
                )}
              </ScrollView>
              <XStack gap={12} paddingTop={8}>
                {isEditingPost ? (
                  <>
                    <Button
                      flex={1}
                      variant="outlined"
                      borderColor={theme ? "#e5e7eb" : "#4b5563"}
                      color={theme ? "#6b7280" : "#9ca3af"}
                      onPress={() => {
                        setIsEditingPost(false);
                        setEditedContent(selectedPost?.content || '');
                      }}
                      disabled={updatingPost}
                    >
                      Cancel
                    </Button>
                    <Button
                      flex={1}
                      backgroundColor={theme ? "#3b82f6" : "#2563eb"}
                      color="white"
                      onPress={async () => {
                        if (!selectedPost?.id && !selectedPost?._id) return;

                        setUpdatingPost(true);
                        try {
                          const postId = selectedPost.id || selectedPost._id;
                          const response = await updateScheduledPost(postId, {
                            content: editedContent.trim(),
                          });

                          if (response.success) {
                            // Update the selected post
                            setSelectedPost({
                              ...selectedPost,
                              content: editedContent.trim(),
                              caption: editedContent.trim(),
                              description: editedContent.trim(),
                            });

                            // Refresh the posts list
                            await fetchScheduledPosts();

                            setIsEditingPost(false);
                            Alert.alert('Success', 'Post updated successfully');
                          } else {
                            Alert.alert('Error', response.message || 'Failed to update post');
                          }
                        } catch (error) {
                          console.error('Error updating post:', error);
                          Alert.alert('Error', 'Failed to update post. Please try again.');
                        } finally {
                          setUpdatingPost(false);
                        }
                      }}
                      disabled={updatingPost || !editedContent.trim()}
                      icon={updatingPost ? (
                        <ActivityIndicator color="white" size="small" />
                      ) : undefined}
                    >
                      {updatingPost ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      flex={1}
                      variant="outlined"
                      borderColor={theme ? "#e5e7eb" : "#4b5563"}
                      color={theme ? "#6b7280" : "#9ca3af"}
                      onPress={() => {
                        setShowPostModal(false);
                        setIsEditingPost(false);
                        setEditedContent('');
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      flex={1}
                      backgroundColor={theme ? "#3b82f6" : "#2563eb"}
                      color="white"
                      onPress={() => {
                        setEditedContent(selectedPost?.content || '');
                        setIsEditingPost(true);
                      }}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </XStack>
            </YStack>
          )}
        </Sheet.Frame>
      </Sheet>

      {/* AI Scheduler Modal */}
      <Modal
        visible={showAIScheduler}
        transparent
        animationType="fade"
        onRequestClose={() => !aiSchedulerLoading && setShowAIScheduler(false)}
      >
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <Pressable
            style={{
              flex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 20,
            }}
            onPress={() => !aiSchedulerLoading && setShowAIScheduler(false)}
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                backgroundColor: theme ? '#ffffff' : '#1f2937',
                borderRadius: 16,
                width: '100%',
                maxWidth: 500,
                maxHeight: '90%',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 8,
              }}
            >
              <Animatable.View animation="slideInUp" duration={300}>
                {/* Header */}
                <XStack
                  padding="$4"
                  alignItems="center"
                  justifyContent="space-between"
                  borderBottomWidth={1}
                  borderBottomColor={theme ? '#e5e7eb' : '#4b5563'}
                >
                  <H4 color={theme ? '#1f2937' : '#f9fafb'}>AI Schedule Post</H4>
                  <Pressable onPress={() => !aiSchedulerLoading && setShowAIScheduler(false)} disabled={aiSchedulerLoading}>
                    <MaterialIcons
                      name="close"
                      size={24}
                      color={theme ? '#6b7280' : '#9ca3af'}
                    />
                  </Pressable>
                </XStack>

                <ScrollView
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={{ padding: 16 }}
                  style={{ maxHeight: 400 }}
                >
                  <YStack gap="$4">
                    {aiSchedulerLoading ? (
                      <YStack alignItems="center" justifyContent="center" minHeight={200} gap="$4">
                        <ActivityIndicator
                          size="large"
                          color={theme ? "#1f2937" : "#f9fafb"}
                        />
                        <SizableText color={theme ? '#6b7280' : '#9ca3af'}>
                          Generating content...
                        </SizableText>
                      </YStack>
                    ) : (
                      <YStack gap="$2">
                        <SizableText color={theme ? '#1f2937' : '#f9fafb'} fontWeight="600">
                          Enter your idea or content
                        </SizableText>
                        <TextArea
                          value={aiSchedulerText}
                          onChangeText={setAiSchedulerText}
                          placeholder="Enter your message or idea to generate scheduled content..."
                          backgroundColor={theme ? '#f8fafc' : '#374151'}
                          borderColor={theme ? '#e5e7eb' : '#4b5563'}
                          minHeight={150}
                          textAlignVertical="top"
                          color={theme ? '#1f2937' : '#ffffff'}
                        />
                      </YStack>
                    )}
                  </YStack>
                </ScrollView>

                {/* Footer */}
                <XStack
                  padding="$4"
                  justifyContent="space-between"
                  borderTopWidth={1}
                  borderTopColor={theme ? '#e5e7eb' : '#4b5563'}
                  gap="$2"
                >
                  <Button
                    flex={1}
                    variant="outlined"
                    borderColor={theme ? '#e5e7eb' : '#4b5563'}
                    color={theme ? '#6b7280' : '#9ca3af'}
                    onPress={() => setShowAIScheduler(false)}
                    disabled={aiSchedulerLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1}
                    backgroundColor="#8b5cf6"
                    color="white"
                    onPress={handleAISchedule}
                    disabled={aiSchedulerLoading}
                    icon={aiSchedulerLoading ? (
                      <ActivityIndicator color="white" size="small" />
                    ) : (
                      <FontAwesome6 name="robot" size={16} />
                    )}
                  >
                    {aiSchedulerLoading ? 'Generating...' : 'Generate & Schedule'}
                  </Button>
                </XStack>
              </Animatable.View>
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
});
export default ScheduleTab;